import { ButtonHTMLAttributes } from "react"
import { styled, StyleProps, CSS, CSSFn, callCSSFn, cssHelper } from "../styles"

export type Variant = `reset` | `brand` | `outline` | `filter` | `pagination` | `navigation`
type Size = `lg`
type Display = `block`
type State = `selected` | `unselected`

export type BaseButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">

export type ButtonStyleProps = {
  variant?: Variant
  size?: Size
  display?: Display
  state?: State
  css?: CSSFn
}
export type StyledButtonProps = BaseButtonProps & ButtonStyleProps

type Props = StyleProps & StyledButtonProps

const getBackgroundColor = (props: Props) => {
  switch (props.variant) {
    case `reset`:
    case `navigation`:
    case `outline`:
      return `transparent`
    case `pagination`:
      return props.theme.palette.white
    case `brand`:
      return props.theme.palette.brand
    default:
      return props.theme.palette.coal.dark
  }
}

const getHoverBackgroundColor = (props: Props) => {
  switch (props.variant) {
    case `reset`:
      return null
    case `outline`:
      return `transparent`
    case `pagination`:
      return props.theme.palette.white
    case `brand`:
      return props.theme.palette.brand
    default:
      return props.theme.palette.coal.light
  }
}

const getDisabledBackgroundColor = (props: Props) => {
  switch (props.variant) {
    case `reset`:
    case `outline`:
      return `transparent`
    case `pagination`:
      return props.theme.palette.white
    default:
      return props.theme.palette.grey.light
  }
}

const getTextColor = (props: Props) => {
  if (props.state === `unselected`) return props.theme.palette.grey.dark
  switch (props.variant) {
    case `reset`:
    case `outline`:
    case `brand`:
    case `pagination`:
      return props.theme.palette.coal.dark
    default:
      return props.theme.palette.white
  }
}

const getHoverTextColor = (props: Props) => {
  switch (props.variant) {
    case `reset`:
    case `outline`:
    case `brand`:
      return props.theme.palette.coal.light
    case `pagination`:
      return props.theme.palette.coal.dark
    default:
      return props.theme.palette.white
  }
}

const getDisabledTextColor = (props: Props) => props.theme.palette.grey.dark

const getBorder = (props: Props) => {
  switch (props.variant) {
    case `outline`:
      return `1px solid ${props.theme.palette.coal.dark}`
    case `pagination`:
      return `1px solid ${props.theme.palette.grey.dark}`
    default:
      return `none`
  }
}

const getFont = (props: Props) => {
  switch (props.variant) {
    case `reset`:
    case `pagination`:
      return { ...props.theme.fonts.body }
    case `filter`:
    default:
      return { ...props.theme.fonts.heading }
  }
}

const getBorderRadius = (props: Props) => (props.variant === `reset` ? 0 : props.size === `lg` ? `72px` : `28px`)

export const getButtonCss = (props: Props) =>
  ({
    ...getFont(props),
    fontSize: props.variant === `brand` || props.variant === `navigation` ? `1rem` : `0.875rem`,
    textTransform: props.variant === `reset` || props.variant === `pagination` ? `inherit` : `uppercase`,
    display: props.variant === `reset` ? `inline` : `inline-flex`,
    height:
      props.variant === `reset`
        ? `auto`
        : props.size === `lg`
        ? `3.375rem`
        : props.variant === `brand` || props.variant === `navigation`
        ? `2.25rem`
        : `2rem`,
    alignItems: `center`,
    justifyContent: `space-around`,
    appearance: `none`,
    boxSizing: `border-box`,
    textAlign: `center`,
    lineHeight: props.variant === `brand` || props.variant === `navigation` ? 1.2 : 1.4,
    width: props.display === `block` ? `100%` : `auto`,
    cursor: `pointer`,
    margin: 0,
    padding:
      props.variant === `brand` || props.variant === `navigation`
        ? `0.46rem 0.938rem 0.375rem`
        : `0.14rem 0.938rem 0.1rem`,
    outline: `none`,
    userSelect: `none`,
    border: getBorder(props),
    borderRadius: getBorderRadius(props),
    color: getTextColor(props),
    background: `unset`,
    backgroundColor: getBackgroundColor(props),
    textDecoration: `none`,
    "&:hover": {
      backgroundColor: getHoverBackgroundColor(props),
      color: getHoverTextColor(props),
      borderColor: props.variant === `outline` ? props.theme.palette.coal.light : `none`,
    },
    "&:disabled": {
      backgroundColor: getDisabledBackgroundColor(props),
      borderColor: props.variant === `outline` ? props.theme.palette.grey.dark : `none`,
      color: getDisabledTextColor(props),
      cursor: `default`,
    },
    ...callCSSFn(props),
  } as CSS)

export const StyledButton = styled.button<StyledButtonProps>(
  (props: Props) =>
    cssHelper`
      ${getButtonCss(props)}
    `
)
